import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
// @ts-ignore
import permissions from 'invitemember-permissions-model';
import ModalAction from '@/components/ModalAction';
import { fetchAPI } from '@/api';
import { AuthState } from '@/types';
import { Input } from '@/components/Form/Input';
import { Loader } from '@/components/Loader';
import CopyCircle from '@/images/icons/CopyCirlce';
import MemberPermissionsList from '@/components/MemberPermissionsList/MemberPermissionsList';

const NewMemberModal = ({ modalOpen, setModalOpen, projectId }: any) => {
	const auth = useSelector((state: { auth: AuthState }) => state.auth);

	const [selectedRole, setSelectedRole] = useState<null | string>(null);
	const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [joinLink, setJoinLink] = useState(null);
	const [isCopied, setIsCopied] = useState(false);
	const [step, setStep] = useState('roleSelect');

	const options = [...permissions.assignableRoles, {
		"name": "Custom",
		"includes": [],
		"key": "CUSTOM"
	}];

	const onClearModal = () => {
		setModalOpen(false);
		setTimeout(() => {
			setIsLoading(false);
		}, 400);
	};

	const onNewMemberAdd = async () => {
		setIsLoading(true);

		let permissions;

		if (selectedRole && selectedRole?.toLowerCase() !== 'custom') {
			permissions = [selectedRole];
		} else {
			permissions = selectedPermissions;
		}

		const response = await fetchAPI("/project/team/member/new", auth?.user?.token, {
			projectId,
			permissions,
		});

		if (!response.ok) {
			throw new Error(`This is an HTTP error: The status is ${response.status}`);
		}

		let data = await response.json();

		if (data.joinLink) {
			setJoinLink(data.joinLink);
		}
		setIsLoading(false);
	}

	const copyToClipboard = () => {
		if (joinLink) {
			setIsCopied(true);
			navigator.clipboard.writeText(joinLink);
		}
	}

	const getDescription = (id: string) => {
		if (id === 'ACCESS_CODE_MANAGER') {
			return `Can create and rollback <a href="https://help.invitemember.com/en/articles/2653926-access-codes-offline-payments-gifts-and-promotions" target="_blank" rel="noreferrer noopener" class="text-[#3CBBCE] underline hover:no-underline hover">access codes</a> only`;
		} else if (id === 'AFP_MANAGER') {
			return `Can change <a href="https://help.invitemember.com/en/articles/4676190-built-in-affiliate-program" target="_blank" rel="noreferrer noopener" class="text-[#3CBBCE] underline hover:no-underline hover">affiliate program</a> settings and do payouts but<br class="hidden md:block" /> doesn't have access to user data and other project settings`;
		} else if (id === 'MANAGER') {
			return `Can change almost everything, but doesn't have<br class="hidden md:block" /> access to <a href="https://help.invitemember.com/en/articles/2933298-what-payment-options-are-available" target="_blank" rel="noreferrer noopener" class="text-[#3CBBCE] underline hover:no-underline hover">payment options</a> and user data`;
		} else if (id === 'ADMINISTRATOR') {
			return `Like Manager but with access to payment options,<br class="hidden md:block" /> user data, and <a href="https://help.invitemember.com/en/articles/2868475-how-to-ban-a-user" target="_blank" rel="noreferrer noopener" class="text-[#3CBBCE] underline hover:no-underline hover">banning</a>/<a href="https://help.invitemember.com/en/articles/4035908-how-to-whitelist-a-user" target="_blank" rel="noreferrer noopener" class="text-[#3CBBCE] underline hover:no-underline hover">whitelisting</a>`;
		}
	};

	const onSubmit = () => {
		if (step === 'roleSelect') {
			setStep('rolesList');
		} else if (step === 'rolesList') {
			setStep('join');
			onNewMemberAdd();
		}
	}

	useEffect(() => {
		if (selectedRole) {
			if (selectedRole.toLowerCase() === 'custom') {
				setSelectedPermissions([]);
			}
		}
	}, [selectedRole]);

	useEffect(() => {
		if (isCopied) {
			setTimeout(() => {
				setIsCopied(false);
			}, 3000);
		}
	}, [isCopied]);

	return (
		<ModalAction
			id="new-member-modal"
			modalOpen={modalOpen}
			setModalOpen={setModalOpen}
			onModalClose={() => onClearModal()}
			size={undefined}
		>
			<>
				{step === 'roleSelect' && (
					<>
						<h2 className="h2 mb-8 pr-16 leading-[0.9] md:pr-0">
							Please select a role for the new team member or click Custom for custom permissions:
						</h2>
						<div className="flex flex-col gap-3 mb-6">
							{options.map((option: any) => (
								<button
									key={option.key}
									className={classNames(
										'flex gap-3 px-4 py-[14px] border rounded-[8px] text-left duration-300 ease-out',
										selectedRole === option.key ? 'bg-[#E2F3F5] border-[#3CBBCE]' : 'border-[#E8E8E8]',
									)}
									onClick={() => setSelectedRole(selectedRole === option.key ? null : option.key)}
								>
									<div className={classNames(
										'w-5 min-w-5 h-5 rounded-full border-[1.5px] flex items-center justify-center transition duration-300 ease-out group-hover:border-brand-green',
										selectedRole === option.key ? 'border-[#3CBBCE]' : 'border-[#E8E8E8]',
									)}>
										<div className={classNames(
											'w-2.5 h-2.5 rounded-full bg-[#3CBBCE] transition duration-300 ease-out',
											selectedRole === option.key ? 'scale-1' : 'scale-0',
										)} />
									</div>
									<div className="flex flex-col gap-2 my-0.5">
										<span className="text-base leading-none font-medium">{option.name}</span>
										{getDescription(option.key) && (
											<span className="text-[11px]/[1.4]">
												<span dangerouslySetInnerHTML={{ __html: getDescription(option.key) || '' }} />
											</span>
										)}
									</div>
								</button>
							))}
						</div>

						<div className="flex items-start gap-2">
							<CopyCircle className="w-4 h-4 text-white mt-px" backgroundColor="#203B3F" />
							<div className="text-[13px]/[1.4]">
								Explore further about roles in our <a href="https://help.invitemember.com/"
								                                      target="_blank"
								                                      rel="noreferrer noopener"
								                                      className="text-[#3CBBCE] underline hover:no-underline hover">Help Center</a>.
							</div>
						</div>
					</>
				)}

				{(step === 'rolesList') && selectedRole?.toLowerCase() !== 'custom' && (
					<>
						<h2 className="h2 mb-6 pr-16 leading-[0.9] md:pr-0">
							Manager permissions:
						</h2>
						<div className={classNames(
							'text-base leading-[1.3] gap-x-5 md:gap-x-4',
							permissions.getUnpackedAndAssignablePermissionsOnly([selectedRole]).length > 10 ? 'grid md:grid-cols-2' : ''
						)}>
							{permissions.getUnpackedAndAssignablePermissionsOnly([selectedRole]).map((permission: any) => (
								<div key={permission}>{permissions[permission].name}</div>
							))}
						</div>
					</>
				)}

				{step === 'rolesList' && selectedRole?.toLowerCase() === 'custom' && (
					<>
						<h2 className="h2 mb-8 pr-16 leading-[0.9] md:pr-0">
							Choose a permissions:
						</h2>
						<MemberPermissionsList
							selectedPermissions={selectedPermissions}
							onChangePermissions={(selected: string[]) => setSelectedPermissions(selected)}
							size="sm"
						/>
					</>
				)}

				{step === 'join' && joinLink && (
					<h2 className="h2 mb-4 leading-[0.9]">
						Please forward this link to your teammate
					</h2>
				)}

				{step === 'join' && joinLink && (
					<>
						<div className="text-[13px]/[1.4] md:text-base mb-6">Please forward this link to your teammate. <br />He just needs to open it and click <span className="text-[#3CBBCE]">START</span>.</div>
						<div className="flex">
							<Input className="h-10 read-only:text-brand-green truncate mr-[-52px] z-[1]"
							       type="text"
							       name="confirmed-username"
							       value={joinLink}
							       inputClassName="text-sm md:text-base !pr-[calc(52px+12px)]"
							       readOnly
							/>
							<button
								className={classNames(
									'w-[105px] min-w-[105px] px-[10px] focus:outline-none relative z-[2]',
									isCopied ? 'btn-primary-inverted bg-[#EAE6E0] border-[#EAE6E0] hover:bg-[#DFD8CD] hover:border-[#DFD8CD]': 'btn-primary',
								)}
								onClick={copyToClipboard}>
								{isCopied ? 'Link copied!' : 'Copy link'}
							</button>
						</div>
						<div className="text-xs md:text-[11px] leading-[1.1] text-[#A5A5A5] pt-1.5 md:pt-2 pl-4">
							This link will expire in 24 hours
						</div>
					</>
				)}

				{isLoading && (
					<div className="text-brand-green w-[50px] h-[50px] my-1 mx-auto">
						<Loader />
					</div>
				)}
				<div className="flex flex-col">
					<button
						className={classNames(
							'btn-primary w-full',
							step === 'join' ? 'sr-only' : 'mt-10',
						)}
						disabled={isLoading || ((step === 'roleSelect') && !selectedRole) || (step === 'rolesList' && selectedRole?.toLowerCase() === 'custom' && selectedPermissions.length === 0)}
						onClick={() => onSubmit()}
					>
						{step === 'roleSelect' && (
							'Next'
						)}
						{step === 'rolesList' && (
							'Ok, next'
						)}
					</button>
					<button
						className={classNames(
							'btn-secondary-inverted w-full',
							(step === 'roleSelect' || step === 'join') ? 'sr-only' : 'h-10 mt-4',
						)}
						onClick={() => setStep('roleSelect')}
					>
						Go back
					</button>
				</div>
			</>
		</ModalAction>
	);
};

export default NewMemberModal;
